import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { classNames } from "@/lib/utils/classNames";
import { Loader } from "lucide-react";

const buttonVariants = cva(
  `
    inline-flex shrink-0 items-center justify-center gap-2 rounded-lg text-sm/5 font-medium
    whitespace-nowrap transition-all outline-none
    hover:cursor-pointer
    focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50
    disabled:pointer-events-none disabled:opacity-50
    aria-invalid:border-destructive aria-invalid:ring-destructive/20
    [&_svg]:pointer-events-none [&_svg]:shrink-0
    [&_svg:not([class*='size-'])]:size-4
  `,
  {
    variants: {
      variant: {
        solid: `
          bg-primary text-primary-foreground shadow-xs
          hover:bg-primary/90
        `,
        destructive: `
          bg-destructive-background text-destructive-foreground shadow-xs
          hover:bg-destructive-background-hover
          focus-visible:ring-solid-error/20
        `,
        outline: `
          text-sm/5 cursor-pointer bg-secondary-background p-3 font-semibold border border-border
          text-secondary-foreground transition-all duration-300
          hover:bg-popover-hover
          disabled:cursor-not-allowed disabled:opacity-50
        `,
        ghost: `text-secondary-foreground hover:bg-popover-hover`,
        link: `
          text-secondary underline
        `,
        primary: `
          cursor-pointer bg-primary p-3 font-semibold 
          text-primary-foreground transition-all duration-300
          hover:bg-primary-background-hover
          disabled:cursor-not-allowed disabled:opacity-50
        `,
        secondary: `
          text-sm/5 cursor-pointer bg-tertiary p-3 font-semibold
          text-secondary-foreground transition-all duration-300
          hover:bg-tertiary-hover
          disabled:cursor-not-allowed disabled:opacity-50
        `,
        unstyled: ``,
      },
      size: {
        default: `
          h-10 py-[10px] px-3
        `,
        sm: `
          h-8 gap-1.5 rounded-lg px-3
          has-[>svg]:px-2.5
        `,
        lg: `
          h-10 rounded-lg px-6
          has-[>svg]:px-4
        `,
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
    loadingText?: string; // optional alternative text
  };

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  loadingText,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={classNames(buttonVariants({ variant, size, className }))}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <Loader className="h-4 w-4 animate-spin" />
          {loadingText ?? children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
